import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

export function CareHomeRegistrationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1: Care Home details
  const [careHomeName, setCareHomeName] = useState('');
  const [address, setAddress] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');

  // Step 2: First Dependent (optional)
  const [dependentName, setDependentName] = useState('');
  const [dependentDob, setDependentDob] = useState('');
  const [dependentNotes, setDependentNotes] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [careHomeId, setCareHomeId] = useState<string | null>(null);

  const handleStep1Submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const careHome = await api.createCareHome({
        name: careHomeName,
        address: address || undefined,
        registrationNumber: registrationNumber || undefined,
      });
      setCareHomeId(careHome.id);
      setStep(2);
    } catch (err: any) {
      setError(err.message || 'Failed to create care home');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (dependentName && careHomeId) {
        await api.createCareHomeDependent(careHomeId, {
          name: dependentName,
          dateOfBirth: dependentDob || undefined,
          notes: dependentNotes || undefined,
        });
      }
      navigate('/staff/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to create dependent');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/staff/dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-center text-gray-900">Care Home Setup</h1>
          <p className="mt-2 text-center text-gray-600">
            Register your care home and start managing care
          </p>
          <div className="mt-6 flex justify-center space-x-2">
            <div className={`h-2 w-16 rounded ${step >= 1 ? 'bg-primary-600' : 'bg-gray-300'}`} />
            <div className={`h-2 w-16 rounded ${step >= 2 ? 'bg-primary-600' : 'bg-gray-300'}`} />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleStep1Submit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-medium text-gray-900">Care Home Details</h2>

              <div>
                <label htmlFor="careHomeName" className="label">
                  Care Home Name *
                </label>
                <input
                  id="careHomeName"
                  name="careHomeName"
                  type="text"
                  required
                  placeholder="e.g., Sunrise Care Home"
                  className="input"
                  value={careHomeName}
                  onChange={(e) => setCareHomeName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="address" className="label">
                  Address
                </label>
                <textarea
                  id="address"
                  name="address"
                  rows={2}
                  className="input"
                  placeholder="Full address..."
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="registrationNumber" className="label">
                  Registration Number
                </label>
                <input
                  id="registrationNumber"
                  name="registrationNumber"
                  type="text"
                  placeholder="Official registration number"
                  className="input"
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                />
                <p className="mt-1 text-sm text-gray-500">
                  CQC or equivalent registration number
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Care Home'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleStep2Submit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-medium text-gray-900">Add First Resident (Optional)</h2>
              <p className="text-sm text-gray-600">
                You can add residents now or later from the dashboard.
              </p>

              <div>
                <label htmlFor="dependentName" className="label">
                  Resident Name
                </label>
                <input
                  id="dependentName"
                  name="dependentName"
                  type="text"
                  className="input"
                  value={dependentName}
                  onChange={(e) => setDependentName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="dependentDob" className="label">
                  Date of Birth
                </label>
                <input
                  id="dependentDob"
                  name="dependentDob"
                  type="date"
                  className="input"
                  value={dependentDob}
                  onChange={(e) => setDependentDob(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="dependentNotes" className="label">
                  Notes
                </label>
                <textarea
                  id="dependentNotes"
                  name="dependentNotes"
                  rows={3}
                  className="input"
                  placeholder="Any important details..."
                  value={dependentNotes}
                  onChange={(e) => setDependentNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 btn btn-secondary"
              >
                Skip for Now
              </button>
              <button
                type="submit"
                disabled={loading || !dependentName}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding...' : 'Add Resident'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
